import { translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Cell, Text } from '@telegram-apps/telegram-ui';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { QuestMetricType } from '../../client/generated/graphql';
import { ListCategory, ListQuest } from '../../client/types';
import { getColor } from '../../helpers/color';
import { mapDifficultyToXp, timeValueToXP } from '../../helpers/experience';
import { CategoryIcon } from './CategoryIcon';
import { CommonIcon } from './CommonIcon';
import { LiquidGlassRowCard } from './LiquidGlassRowCard';

export type ConditionQuestForBlock = Pick<
  ListQuest,
  'name' | 'timing' | 'timedInterval' | 'timerValueInMs' | 'difficulty' | 'metric' | 'repeat' | 'templateId'
>;

interface ConditionQuestBlockProps {
  quest: ConditionQuestForBlock;
  category: ListCategory;
  isSelected: boolean;
  onToggle: () => void;
  metricType: QuestMetricType;
}

export const ConditionQuestBlock: FC<ConditionQuestBlockProps> = ({
  quest,
  category,
  isSelected,
  onToggle,
  metricType,
}) => {
  const { t } = useTranslation();
  const questXP = useMemo(() =>
    !quest.timing || quest.timedInterval === 0
      ? mapDifficultyToXp(quest.difficulty)
      : timeValueToXP((quest.timedInterval || 0) * 60 * 1000), // Convert minutes to milliseconds
    [quest.timing, quest.timedInterval, quest.difficulty]
  );

  const getCurrentMetricValue = (): number => {
    switch (metricType) {
      case QuestMetricType.Experience:
        return quest.metric?.xpPoints || 0;
      case QuestMetricType.Time:
        return Math.floor((quest.metric?.timeSpentSec || 0) / 3600);
      case QuestMetricType.Completion:
        return quest.metric?.completed ? 1 : 0;
      case QuestMetricType.Repeating:
        return quest.metric?.completedTimes || 0;
      default:
        return 0;
    }
  };

  const getMetricDisplayText = (): string => {
    const currentValue = getCurrentMetricValue();
    switch (metricType) {
      case QuestMetricType.Experience:
        return `Received ${currentValue} XP`;
      case QuestMetricType.Time:
        return `Completed ${currentValue} hours`;
      case QuestMetricType.Completion:
        return quest.metric?.completed ? 'Completed' : 'Not completed';
      case QuestMetricType.Repeating:
        return `Completed ${currentValue} times`;
      default:
        return '';
    }
  };

  const getMetricAfterContent = () => {
    switch (metricType) {
      case QuestMetricType.Experience:
        return (
          <Text style={{ fontSize: '14px', whiteSpace: 'nowrap', color: 'var(--tgui--hint_color)' }}>
            +{questXP} XP
          </Text>
        );
      case QuestMetricType.Time:
        const hours = quest.timedInterval ? quest.timedInterval / 60 :
                      quest.timing && quest.timerValueInMs ? (quest.timerValueInMs / (1000 * 60 * 60)) : 0;
        return (
          <Text style={{ fontSize: '14px', whiteSpace: 'nowrap', color: 'var(--tgui--hint_color)' }}>
            {hours > 0 ? `${hours.toFixed(1)}h` : ''}
          </Text>
        );
      case QuestMetricType.Completion:
        return (
          <CommonIcon
            icon={quest.metric?.completed ? "check_circle" : "radio_button_unchecked"}
            type="filled"
            size={20}
            color={quest.metric?.completed ? "var(--tg-theme-button-color)" : "var(--tgui--hint_color)"}
          />
        );
      case QuestMetricType.Repeating:
        return (
          <CommonIcon
            icon={quest.repeat ? "repeat" : "repeat_one"}
            type="filled"
            size={20}
            color={quest.repeat ? "var(--tg-theme-button-color)" : "var(--tgui--hint_color)"}
          />
        );
      default:
        return null;
    }
  };

  return (
    <LiquidGlassRowCard style={{ marginBottom: '8px' }}>
        <Cell
        onClick={onToggle}
        style={{
            backgroundColor: isSelected ? 'color-mix(in srgb, var(--tg-theme-button-color) 12%, transparent)' : 'transparent',
            transition: 'background-color 0.2s ease',
        }}
        after={
            <Stack direction="row" alignItems="center" gap="10px">
                {getMetricAfterContent()}
                <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: `2px solid ${isSelected ? 'var(--tg-theme-button-color)' : 'var(--tgui--hint_color)'}`,
                    backgroundColor: isSelected ? 'var(--tg-theme-button-color)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {isSelected && (
                        <CommonIcon icon="check" type="filled" size={16} color="var(--tg-theme-button-text-color)" />
                    )}
                </Box>
            </Stack>
        }
        subtitle={
            <Stack direction="row" alignItems="center" gap="4px">
                <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 1,
                    background: getColor(category.iconColor),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CategoryIcon
                        icon={category.icon}
                        iconColor={category.iconColor}
                        customIcon={category.customIcon}
                        type="filled"
                        size={12}
                        color="var(--tg-theme-text-color)"
                    />
                </Box>
                <Text style={{
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    color: "var(--tgui--hint_color)"
                }}>
                    {translateTemplateText(t, category.templateId, 'name', category.name)}
                </Text>
                <Text style={{
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    color: "var(--tgui--hint_color)",
                }}>• {getMetricDisplayText()}</Text>
            </Stack>
        }
        >
        <Text weight="2" style={{ color: 'var(--tg-theme-text-color)', fontSize: '16px' }}>
            {translateTemplateText(t, quest.templateId, 'name', quest.name)}
        </Text>
        </Cell>
    </LiquidGlassRowCard>
  );
};
